import { Theme, css, useTheme } from '@emotion/react';

import { PropsWithChildren, ReactNode } from 'react';

import { Spacing } from 'chatie-design-system';
import { Close, Icon } from 'chatie-design-system/icon';

import { MODAL_HEADER_HEIGHT, safeArea } from '@styles/css';
import { CSSProps, OnClickProps } from '@utils/misc';

import Push, { PushSize } from '../layout/Push';
import Button, { ButtonProps } from './Button';
import Overlay, { OverlayProps } from './Overlay';

const fullscreenModalCSS = (isOpen: boolean) => (theme: Theme) =>
  css`
    width: 100%;
    margin: 0 auto;
    position: relative;
    height: 100%;
    background-color: ${theme.var.white};
    transition: 0.4s cubic-bezier(0.25, 1, 0.5, 1);
    will-change: transform;

    ${isOpen
      ? css`
          transform: translateY(0);
          visibility: visible;
        `
      : css`
          transform: translateY(100%);
          visibility: hidden;
        `}
  `;

const headerWrapperCSS = (theme: Theme) => css`
  position: fixed;
  top: 0;
  z-index: 9;
  width: 100%;
  background-color: ${theme.var.white};
  ${safeArea('top')}
`;

const headerCSS = (isHideHeaderBorder: boolean) => (theme: Theme) =>
  css`
    position: relative;
    justify-content: space-between;
    padding: 0 ${Spacing.medium}px;
    height: ${MODAL_HEADER_HEIGHT}px;
    ${!isHideHeaderBorder &&
    css`
      border-bottom: 1px solid ${theme.border.light};
    `}

    h4 {
      text-align: center;
      line-height: ${MODAL_HEADER_HEIGHT}px;
    }
  `;

const headerLeftButtonCSS = css`
  position: absolute;
  top: 10px;
  left: ${Spacing.small}px;
`;

const headerCloseButtonCSS = css`
  position: absolute;
  top: 10px;
  right: ${Spacing.small}px;
`;

const contentCSS = (theme: Theme) =>
  css`
    position: relative;
    display: block;
    width: 100%;
    height: 100%;
    max-height: inherit;
    overflow: auto;
    background-color: ${theme.var.white};
  `;

export interface FullscreenModalProps extends OverlayProps {
  title?: string;
  leftButton?: (color: string) => ReactNode;
  rightButton?: (color: string) => ReactNode;
  renderButton?: () => ReactNode;
  hideHeaderBorder?: boolean;
}

const FullscreenModal = ({
  title,
  children,
  leftButton,
  rightButton,
  renderButton,
  hideHeaderBorder = false,
  className,
  ...rest
}: PropsWithChildren<FullscreenModalProps> & CSSProps) => {
  const { open, onClose } = rest;

  const theme = useTheme();

  return (
    <Overlay {...rest} blocking={false} className={className}>
      <div css={fullscreenModalCSS(open)}>
        <header css={headerWrapperCSS}>
          <div css={headerCSS(hideHeaderBorder)}>
            {leftButton && (
              <div css={headerLeftButtonCSS}>{leftButton(theme.var.black)}</div>
            )}
            <h4>{title}</h4>
            <div css={headerCloseButtonCSS}>
              {rightButton ? (
                rightButton(theme.var.black)
              ) : (
                <button
                  className="icon-btn"
                  type="button"
                  onClick={() => onClose()}
                >
                  <Icon icon={Close} color={theme.var.black} />
                </button>
              )}
            </div>
          </div>
        </header>

        <div css={contentCSS}>
          <Push top size={PushSize.header} />
          {children}
          {renderButton && <Push bottom size={PushSize.bottomButton} />}
        </div>
        {renderButton?.()}
      </div>
    </Overlay>
  );
};

/** Button */
const fullscreenModalButtonCSS = css`
  position: absolute;
  width: 100%;
  left: 0;
  bottom: 0;
  padding-left: ${Spacing.medium}px;
  padding-right: ${Spacing.medium}px;
  z-index: 9;
  ${safeArea('bottom', 'padding', Spacing.medium)}

  @media (max-height: 340px) {
    display: none;
  }
`;

export const FullscreenModalButton = ({
  className,
  text,
  disabled = false,
  onClick,
}: ButtonProps & OnClickProps & CSSProps) => {
  return (
    <div css={fullscreenModalButtonCSS} className={className}>
      <Button text={text} disabled={disabled} onClick={onClick} />
    </div>
  );
};

export default FullscreenModal;
